// Crash-surviving diagnostics for the M2C2 battery.
//
// WHY localStorage AND NOT the console, IndexedDB, or React state:
//
// The Android failure being chased here freezes the whole tab — the parent
// app's own UI stops repainting, so an on-screen debug panel is unreachable
// exactly when it matters, and React state dies with the page. IndexedDB is
// asynchronous, so a write issued moments before the freeze may never commit.
// localStorage is the one storage API that is synchronous AND persists across
// a force-quit: by the time setItem() returns, the line is durable. That is
// the whole reason it's used here despite being otherwise unfashionable.
//
// So the workflow after a freeze is: force-quit the app, reopen it, and read
// the log from the researcher panel. The last line written is the last thing
// that happened before the phone went under.

const KEY = 'aspire_m2c2_log_v1';
const RUN_KEY = 'aspire_m2c2_run_v1';
const MAX_LINES = 400;

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function write(lines) {
  try {
    localStorage.setItem(KEY, JSON.stringify(lines));
  } catch (e) {
    // Quota exhausted or storage disabled. Drop the oldest half and retry
    // once — losing old lines is much better than losing the ones being
    // written right now, which are the ones describing the failure.
    try {
      localStorage.setItem(KEY, JSON.stringify(lines.slice(-Math.floor(MAX_LINES / 2))));
    } catch (e2) {
      // Nothing further to do; the in-memory panel still works.
    }
  }
}

/** Wipe the log and stamp a new run. Called once when a battery starts. */
export function startRun(meta) {
  try {
    localStorage.setItem(RUN_KEY, JSON.stringify({ started: new Date().toISOString(), ...meta }));
  } catch (e) { /* non-fatal */ }
  write([]);
  logLine(`=== battery run started === ${JSON.stringify(meta)}`);
}

/** Append one line. Synchronous and durable by design — see header. */
export function logLine(text) {
  const stamped = `${new Date().toISOString().slice(11, 23)} ${text}`;
  // Keep the console in sync so a USB-attached devtools session and the
  // persisted log tell the same story rather than two half-stories.
  try {
    console.log(`[m2c2] ${text}`);
  } catch (e) { /* non-fatal */ }
  const lines = read();
  lines.push(stamped);
  write(lines.length > MAX_LINES ? lines.slice(-MAX_LINES) : lines);
  return stamped;
}

export function readLog() {
  return read();
}

export function readRunMeta() {
  try {
    const raw = localStorage.getItem(RUN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function clearLog() {
  write([]);
}

/**
 * Create a throwaway WebGL context, report what the driver says, then throw
 * it away again.
 *
 * This is the measurement that distinguishes the two live theories. If the
 * probe succeeds before activity 1 but returns null before activity 2, then
 * the device genuinely cannot hand out another WebGL context at that moment —
 * GPU/context exhaustion, proven rather than assumed. If it succeeds both
 * times, the freeze is somewhere else entirely and we stop chasing WebGL.
 *
 * The probe releases its own context immediately via WEBGL_lose_context so it
 * can't itself become part of the problem it is measuring.
 */
export function probeWebGL(label) {
  let canvas = null;
  try {
    canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const gl =
      canvas.getContext('webgl2') ||
      canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl');
    if (!gl) {
      logLine(`WEBGL PROBE (${label}): FAILED — no context available`);
      return false;
    }
    let renderer = 'unknown';
    try {
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      renderer = ext
        ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL))
        : String(gl.getParameter(gl.RENDERER));
    } catch (e) { /* renderer string is a nicety, not the point */ }
    logLine(
      `WEBGL PROBE (${label}): ok — ${renderer} · maxTex ${gl.getParameter(gl.MAX_TEXTURE_SIZE)}`
    );
    try {
      const lose = gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
    } catch (e) { /* best effort */ }
    return true;
  } catch (err) {
    logLine(`WEBGL PROBE (${label}): threw — ${String(err)}`);
    return false;
  } finally {
    if (canvas) {
      canvas.width = 1;
      canvas.height = 1;
    }
  }
}

/** Rough memory reading where the browser exposes it (Chrome does; Safari doesn't). */
export function memoryNote() {
  try {
    const m = performance.memory;
    if (!m) return '';
    const mb = (n) => Math.round(n / 1048576);
    return `jsHeap ${mb(m.usedJSHeapSize)}/${mb(m.jsHeapSizeLimit)}MB`;
  } catch (e) {
    return '';
  }
}
